import React, { useState, useMemo, useEffect } from "react";
import MaterialReactTable from "material-react-table";
import { GlobalStyles } from "@mui/material";

const CustomMaterialTable = ({
  columns,
  data,
  manualPagination,
  rowCount,
  paginationChange,
  soringChange,
  searchChange,
  searchText,
  sorting,
  page,
  pagesize,
  paginataion,
  darkMode,
  exportExcel,
  setVisibleColumns,
  setFilteredData,
  toggleDensity,
  ...rest
}) => {
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = (event) => {
    setIsScrolled(event.currentTarget.scrollTop > 0);
  };

  const normalizeText = (value) => {
    if (value === null || value === undefined) return "";

    return String(value)
      .toLowerCase()
      .trim()                // remove leading & trailing spaces
      .replace(/\s+/g, " "); // collapse multiple spaces
  };

  const filteredRows = useMemo(() => {
    const normalizedSearch = normalizeText(searchText);
    const searchWords = normalizedSearch.split(" ").filter(Boolean);

    return data.filter((row) => {
      // Column filters (unchanged)
      const columnMatch = columnFilters.every(({ id, value }) =>
        normalizeText(row[id]).includes(normalizeText(value))
      );

      // Global search: ALL words must match somewhere in the row
      const globalMatch =
        searchWords.length > 0
          ? searchWords.every((word) =>
            Object.values(row).some((val) =>
              normalizeText(val).includes(word)
            )
          )
          : true;

      return columnMatch && globalMatch;
    });
  }, [data, columnFilters, searchText]);

  const styledColumns = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      size: col.size || 125, // Default size if not provided
    }));
  }, [columns]);

  // Update parent with filtered data
  useEffect(() => {
    if (exportExcel) {
      setFilteredData(filteredRows);
    }
  }, [filteredRows, exportExcel, setFilteredData]);
  return (
    <>
      <style>
        {`
     .MuiTableHead-root .MuiTableRow-root {
     box-shadow: ${isScrolled ? "0px 2px 1px -1px #5cb360" : "none"} !important;
      transition: 'box-shadow 1s ease-in-out';
    }
  `}
      </style>
      <MaterialReactTable
        enableStickyHeader
        enableColumnResizing
        columnResizeMode="onChange"
        defaultColumn={{
          minSize: 100,
          size: 200,
          maxSize: 400,
        }}
        columns={styledColumns}
        enableDensityToggle={false}
        data={filteredRows}
        manualPagination={manualPagination}
        rowCount={rowCount}
        onPaginationChange={paginationChange}
        page={page}
        pageSize={pagesize}
        setPagination={paginataion}
        onSortingChange={soringChange}
        sorting={sorting}
        initialState={{
          showColumnFilters: false,
          density: "compact", // Default to High Density
          pagination: { pageSize: 20 },
        }}
        muiTableHeadCellFilterTextFieldProps={({ column }) => ({
          placeholder: `Filter`,
          sx: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: "6px",
            fontWeight: "200",
            width: "20px",
            textTransform: "none",
            "& .MuiInputLabel-root": {
              textTransform: "none",
            }
          },
        })}
        muiTableContainerProps={{
          onScroll: handleScroll,
          sx: {
            maxHeight: "700px", // Set a max height for the table container
          },
        }}
        muiNoDataRowProps={{
          sx: {
            backgroundColor: darkMode ? "#202940" : "#fff",
          },
        }}
        muiNoDataCellProps={{
          sx: {
            color: darkMode ? "#ffffff" : "#000000",
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: "14px",
            fontWeight: "500",
          },
        }}
        muiTableProps={{
          sx: darkMode
            ? {
              backgroundColor: "#202940",
              color: "#ffffff",
              "& td": {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: "14px",
                fontWeight: "500",
                lineHeight: "17.07px",
                color: "#ffffff",
              },
            }
            : {
              "& td": {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: "14px",
                fontWeight: "500",
                lineHeight: "17.07px",
                backgroundColor: "#f5f5f5",
              },
            },
        }}

        muiTablePaginationProps={{
          rowsPerPageOptions: [5, 10, 20, 50],
          showFirstButton: true,
          showLastButton: true,
          count: rowCount,
          page: page,
          rowsPerPage: pagesize,
        }}
        muiTopToolbarProps={{
          sx: darkMode
            ? {
              color: "#ffffff",
              "& svg": {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: "14px",
                fontWeight: "500",
                lineHeight: "17.07px",
                color: "#ffffff",
              },
            }
            : {
              backgroundColor: "#f5f5f5",
            },
        }}
        muiTableHeadCellProps={{
          sx: {
            ...(darkMode
              ? {
                color: "#ffffff",
                "& svg": {
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "17.07px",
                  color: "#ffffff",
                },
              }
              : {
                backgroundColor: "#f5f5f5",
              }),
          },
        }}
        muiBottomToolbarProps={{
          sx: darkMode
            ? {
              color: "#ffffff",
              "& p,button,div": {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: "500",
                lineHeight: "17.07px",
                color: "#ffffff",
              },
            }
            : {
              backgroundColor: "#f5f5f5",
            },
        }}
        muiTableBodyCellProps={{
          sx: {
            borderBottom: "2px solid #e0e0e0", // Add a border between columns
            padding: "4px 8px", // Reduce vertical & horizontal padding
            width: "100px",
            whiteSpace: "normal", // allow text to wrap
            wordBreak: "break-word", // break long words
            overflowWrap: "break-word",
          },
        }}
        state={{
          columnVisibility,
          // globalFilter: searchText,
          columnFilters,
        }}
        onColumnVisibilityChange={(newState) => {
          if (typeof setColumnVisibility === "function") {
            setColumnVisibility(newState);
          }
          if (typeof setVisibleColumns === "function") {
            setVisibleColumns(newState);
          }
        }}
        onGlobalFilterChange={(newValue) =>
          searchChange((newValue || "").trim())
        }
        onColumnFiltersChange={setColumnFilters}
        {...rest} // Pass other props dynamically
      />
      {darkMode && (
        <GlobalStyles
          styles={{
            ".MuiTypography-body1": {
              color: "#ffffff !important", // Change the icon color to white
            },
          }}
        />
      )}
    </>
  );
};

export default CustomMaterialTable;
